import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const AdminLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [mfaToken, setMfaToken] = useState('');
    const [requiresMFA, setRequiresMFA] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();
    // Redirect to dashboard if already logged in
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            navigate('/admin/dashboard');
        }
    }, [navigate]);
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const body = { email, password };
            if (requiresMFA && mfaToken) {
                body.mfaToken = mfaToken;
            }
            const response = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                credentials: 'include',
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Login failed');
            }
            // Check if MFA is required
            if (data.requiresMFA) {
                setRequiresMFA(true);
                toast({
                    title: 'MFA Required',
                    description: 'Please enter your 6-digit code from Google Authenticator',
                });
                setLoading(false);
                return;
            }
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            toast({
                title: 'Success',
                description: 'Logged in successfully',
            });
            navigate('/admin/dashboard');
        }
        catch (error) {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-card", children: _jsxs(Card, { className: "w-full max-w-md", children: [_jsxs(CardHeader, { children: [_jsx(CardTitle, { className: "text-2xl", children: "Admin Login" }), _jsx(CardDescription, { children: "Enter your credentials to access the admin panel" })] }), _jsx(CardContent, { children: _jsxs("form", { onSubmit: handleLogin, className: "space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "email", children: "Email" }), _jsx(Input, { id: "email", type: "email", placeholder: "admin@qbcron.com", value: email, onChange: (e) => setEmail(e.target.value), required: true, disabled: requiresMFA })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "password", children: "Password" }), _jsx(Input, { id: "password", type: "password", value: password, onChange: (e) => setPassword(e.target.value), required: true, disabled: requiresMFA })] }), requiresMFA && (_jsxs("div", { className: "space-y-2", children: [_jsxs(Label, { htmlFor: "mfaToken", className: "flex items-center gap-2", children: [_jsx("span", { className: "text-2xl", children: "\uD83D\uDD10" }), "Google Authenticator Code"] }), _jsx(Input, { id: "mfaToken", type: "text", placeholder: "000000", value: mfaToken, onChange: (e) => setMfaToken(e.target.value.replace(/\D/g, '').slice(0, 6)), maxLength: 6, className: "text-center text-2xl tracking-widest font-mono", required: true, autoFocus: true }), _jsx("p", { className: "text-sm text-muted-foreground text-center", children: "Enter the 6-digit code from your authenticator app" })] })), _jsx(Button, { type: "submit", className: "w-full", disabled: loading || (requiresMFA && mfaToken.length !== 6), children: loading ? 'Verifying...' : requiresMFA ? 'Verify & Login' : 'Login' }), requiresMFA && (_jsx(Button, { type: "button", variant: "outline", className: "w-full", onClick: () => {
                                    setRequiresMFA(false);
                                    setMfaToken('');
                                }, children: "\u2190 Back to Login" }))] }) })] }) }));
};
export default AdminLogin;
